import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AppRouteUi, VriApi, AppConstants, CallStatusType, CallType } from "../../configs";
import { ActionVideoCall, ActionUi, AxiosAuth, useAppSelector, VRIAppStateType } from "../../core";
import { ContactInfo } from "../../models/api/videoconference";

type ConferenceState = {
  roomId: string;
  contactInfo: ContactInfo[];
  interpreterRequired: boolean;
  language?: string;
  languageName?: string;
  skill?: string;
  gender?: string;
  department?: string;
  location?: string;
  customFields?: any[];
  isBusy: boolean;
};

const initialState = (): ConferenceState => ({
  roomId: "",
  contactInfo: [{ email: "", phone: "" }],
  interpreterRequired: false,
  customFields: [],
  isBusy: false,
});

export function useInstantConference() {
  const [state, setState] = useState(initialState);
  const [generateButtonDisable, setGenerateButtonDisable] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const userTimeZone = useAppSelector((state: VRIAppStateType) => state.auth.profile?.timeZone);

  const setField = <K extends keyof ConferenceState>(key: K, value: ConferenceState[K]) => {
    setState(previous => ({ ...previous, [key]: value }));
  };

  const onStartConference = async () => {
    if (!state.roomId) {
      toast.error("Please generate room link!");
      return;
    }
    setField("isBusy", true);
    const body = new FormData();
    body.append("roomId", state.roomId);
    body.append("contactInfo", JSON.stringify(state.contactInfo));
    // The shared server endpoint still requires this discriminator.
    body.append("conferenceType", "instantConference");
    body.append("timeZone", userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (state.interpreterRequired) {
      body.append("interpreterRequired", "true");
      body.append("language", state.language ?? "");
      body.append("skill", state.skill ?? "");
      body.append("gender", state.gender ?? "");
      body.append("department", state.department ?? "");
      body.append("location", state.location ?? "");
      body.append("customFields", JSON.stringify(state.customFields));
    }

    try {
      const response = await AxiosAuth.post(VriApi.Conference.CreateConference.Root(), body);
      if (response.status === AppConstants.Api.StatusOK) {
        toast.success("Room Creation Successful");
        setGenerateButtonDisable(false);
        dispatch(ActionVideoCall.SetCallStatus({
          prev: CallStatusType.Received,
          current: CallStatusType.Established,
        }));
        dispatch(ActionUi.SetPreviousRoute(history.location.pathname));
        dispatch(ActionUi.SetRoomId(state.roomId));
        history.push({
          pathname: AppRouteUi.VideoConference.Room.Load(state.roomId, state.interpreterRequired),
          state: { callType: CallType.VideoConference, interpreterRequired: state.interpreterRequired },
        });
        setState(initialState());
      } else {
        toast.error("Room Creation Failed");
      }
    } catch {
      toast.error("Room Creation Failed");
    } finally {
      setField("isBusy", false);
    }
  };

  return {
    state,
    generateButtonDisable,
    setGenerateButtonDisable,
    setRoomId: (value: string) => setField("roomId", value),
    setContactInfo: (value: ContactInfo[]) => setField("contactInfo", value),
    setInterpreterRequired: (value: boolean) => setField("interpreterRequired", value),
    setLanguage: (value?: string) => setField("language", value),
    setLanguageName: (value?: string) => setField("languageName", value),
    setSkill: (value?: string) => setField("skill", value),
    setGender: (value?: string) => setField("gender", value),
    setDepartment: (value?: string) => setField("department", value),
    setLocation: (value?: string) => setField("location", value),
    setCustomFields: (value?: any[]) => setField("customFields", value),
    onStartConference,
  };
}
